import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface PaymentRecord {
  id: string;
  user_id: string;
  payment_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_type: string;
  plan_type: string | null;
  credits: number;
  extra_messages: number;
  description: string | null;
  created_at: string;
  company_name?: string;
  user_name?: string;
  user_email?: string;
}

const statusLabels: Record<string, string> = {
  approved: 'Aprovado',
  pending: 'Pendente',
  rejected: 'Rejeitado',
  refunded: 'Reembolsado',
  cancelled: 'Cancelado',
  in_process: 'Em Processo',
  charged_back: 'Chargeback',
};

const statusColors: Record<string, string> = {
  approved: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  pending: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400',
  rejected: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400',
  refunded: 'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400',
  cancelled: 'bg-foreground-100 dark:bg-foreground-200 text-foreground-500',
  in_process: 'bg-secondary-100 dark:bg-secondary-500/15 text-secondary-700 dark:text-secondary-400',
  charged_back: 'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400',
};

const typeLabels: Record<string, string> = {
  plan: 'Plano',
  credits: 'Créditos',
  excess: 'Excedente',
  setup: 'Implantação',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

// Mock all-system payments for demo
const mockAllPayments: PaymentRecord[] = [
  { id: 'a1', user_id: 'u1', payment_id: 'MP-9876543210', amount: 297.00, currency: 'BRL', status: 'approved', payment_type: 'plan', plan_type: 'professional', credits: 0, extra_messages: 0, description: 'Plano Professional — Renovação', created_at: '2026-08-04T14:22:00Z', user_name: 'Ricardo Almeida', user_email: 'ricardo@agenciax.com.br', company_name: 'Agência X' },
  { id: 'a2', user_id: 'u2', payment_id: 'MP-9876543211', amount: 97.00, currency: 'BRL', status: 'approved', payment_type: 'plan', plan_type: 'starter', credits: 0, extra_messages: 0, description: 'Plano Starter — Assinatura', created_at: '2026-08-04T10:15:00Z', user_name: 'Mariana Costa', user_email: 'mariana@lojabc.com.br', company_name: 'Loja BC' },
  { id: 'a3', user_id: 'u3', payment_id: 'MP-9876543212', amount: 60.00, currency: 'BRL', status: 'approved', payment_type: 'credits', plan_type: null, credits: 10000, extra_messages: 0, description: 'Pacote 10.000 créditos', created_at: '2026-08-04T09:30:00Z', user_name: 'Carlos Mendes', user_email: 'carlos@saudedigital.com', company_name: 'Saúde Digital' },
  { id: 'a4', user_id: 'u4', payment_id: 'MP-9876543213', amount: 997.00, currency: 'BRL', status: 'approved', payment_type: 'setup', plan_type: null, credits: 0, extra_messages: 0, description: 'Implantação Completa', created_at: '2026-08-03T16:45:00Z', user_name: 'Fernanda Lima', user_email: 'fernanda@imobprime.com.br', company_name: 'ImobPrime' },
  { id: 'a5', user_id: 'u5', payment_id: 'MP-9876543214', amount: 450.00, currency: 'BRL', status: 'approved', payment_type: 'plan', plan_type: 'business', credits: 0, extra_messages: 0, description: 'Plano Business — Renovação', created_at: '2026-08-03T11:00:00Z', user_name: 'Gustavo Torres', user_email: 'gustavo@multicanal.com', company_name: 'Multicanal' },
  { id: 'a6', user_id: 'u1', payment_id: 'MP-9876543215', amount: 250.00, currency: 'BRL', status: 'pending', payment_type: 'credits', plan_type: null, credits: 50000, extra_messages: 0, description: 'Pacote 50.000 créditos', created_at: '2026-08-03T08:20:00Z', user_name: 'Ricardo Almeida', user_email: 'ricardo@agenciax.com.br', company_name: 'Agência X' },
  { id: 'a7', user_id: 'u6', payment_id: 'MP-9876543216', amount: 150.00, currency: 'BRL', status: 'approved', payment_type: 'excess', plan_type: null, credits: 0, extra_messages: 30000, description: 'Excedente: +30.000 mensagens', created_at: '2026-08-02T19:10:00Z', user_name: 'Patrícia Souza', user_email: 'patricia@consultrh.com', company_name: 'Consult RH' },
  { id: 'a8', user_id: 'u2', payment_id: 'MP-9876543217', amount: 60.00, currency: 'BRL', status: 'refunded', payment_type: 'credits', plan_type: null, credits: 10000, extra_messages: 0, description: 'Pacote 10.000 créditos — Reembolsado', created_at: '2026-08-02T14:30:00Z', user_name: 'Mariana Costa', user_email: 'mariana@lojabc.com.br', company_name: 'Loja BC' },
  { id: 'a9', user_id: 'u7', payment_id: 'MP-9876543218', amount: 297.00, currency: 'BRL', status: 'approved', payment_type: 'plan', plan_type: 'professional', credits: 0, extra_messages: 0, description: 'Plano Professional — Primeira assinatura', created_at: '2026-08-01T17:00:00Z', user_name: 'André Ribeiro', user_email: 'andre@techstart.com', company_name: 'TechStart' },
  { id: 'a10', user_id: 'u8', payment_id: 'MP-9876543219', amount: 97.00, currency: 'BRL', status: 'rejected', payment_type: 'plan', plan_type: 'starter', credits: 0, extra_messages: 0, description: 'Plano Starter — Pagamento recusado', created_at: '2026-08-01T12:45:00Z', user_name: 'Juliana Freitas', user_email: 'ju@petlove.com.br', company_name: 'PetLove' },
  { id: 'a11', user_id: 'u3', payment_id: 'MP-9876543220', amount: 120.00, currency: 'BRL', status: 'approved', payment_type: 'credits', plan_type: null, credits: 25000, extra_messages: 0, description: 'Pacote 25.000 créditos', created_at: '2026-07-31T10:00:00Z', user_name: 'Carlos Mendes', user_email: 'carlos@saudedigital.com', company_name: 'Saúde Digital' },
  { id: 'a12', user_id: 'u9', payment_id: 'MP-9876543221', amount: 1500.00, currency: 'BRL', status: 'approved', payment_type: 'plan', plan_type: 'enterprise', credits: 0, extra_messages: 0, description: 'Plano Enterprise — Anual', created_at: '2026-07-30T09:15:00Z', user_name: 'Marcos Vinícius', user_email: 'marcos@gruponova.com', company_name: 'Grupo Nova' },
];

export default function AdminInvoicesPage() {
  const { profile } = useAuth();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 15;

  const fetchAllPayments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        // Enrich with user data
        const userIds = [...new Set(data.map((p: PaymentRecord) => p.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        const profileMap = new Map<string, string>();
        if (profiles) {
          profiles.forEach((p: { id: string; full_name: string }) => profileMap.set(p.id, p.full_name));
        }

        const enriched = data.map((p: PaymentRecord) => ({
          ...p,
          user_name: profileMap.get(p.user_id) || 'Desconhecido',
          user_email: '',
          company_name: '',
        }));

        setPayments(enriched as PaymentRecord[]);
      } else {
        // Use mock data
        setPayments(mockAllPayments);
      }
    } catch (err: unknown) {
      console.error('Error fetching all payments:', err);
      setPayments(mockAllPayments);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllPayments();
  }, [fetchAllPayments]);

  if (profile && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Filter
  const filtered = payments.filter((p) => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterType !== 'all' && p.payment_type !== filterType) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesName = (p.user_name || '').toLowerCase().includes(term);
      const matchesEmail = (p.user_email || '').toLowerCase().includes(term);
      const matchesDesc = (p.description || '').toLowerCase().includes(term);
      const matchesPaymentId = p.payment_id.toLowerCase().includes(term);
      if (!matchesName && !matchesEmail && !matchesDesc && !matchesPaymentId) return false;
    }
    return true;
  });

  // Stats
  const totalApproved = payments
    .filter((p) => p.status === 'approved')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const totalPending = payments
    .filter((p) => p.status === 'pending' || p.status === 'in_process')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const totalRejected = payments
    .filter((p) => p.status === 'rejected' || p.status === 'refunded' || p.status === 'charged_back')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  // Pagination
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
            Faturas do Sistema
          </h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Visão global de todas as cobranças e transações da plataforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAllPayments}
            disabled={loading}
            className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-40"
          >
            <i className={`ri-refresh-line text-base ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
              <i className="ri-check-double-line text-lg text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-foreground-500 uppercase">Aprovado</p>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{formatCurrency(totalApproved)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
              <i className="ri-time-line text-lg text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-foreground-500 uppercase">Pendente</p>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{formatCurrency(totalPending)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
              <i className="ri-close-circle-line text-lg text-red-500 dark:text-red-400" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-foreground-500 uppercase">Rejeitado/Devolvido</p>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{formatCurrency(totalRejected)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
              <i className="ri-exchange-funds-line text-lg text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-foreground-500 uppercase">Total Transações</p>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{payments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Buscar por nome, email, descrição ou ID..."
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all"
            />
          </div>

          {/* Status filter */}
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 pr-8 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-700 dark:text-foreground-300 outline-none focus:border-primary-400 cursor-pointer appearance-none"
            >
              <option value="all">Todos os status</option>
              {Object.entries(statusLabels).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-sm" />
          </div>

          {/* Type filter */}
          <div className="relative">
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 pr-8 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-700 dark:text-foreground-300 outline-none focus:border-primary-400 cursor-pointer appearance-none"
            >
              <option value="all">Todos os tipos</option>
              {Object.entries(typeLabels).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-sm" />
          </div>

          {(filterStatus !== 'all' || filterType !== 'all' || searchTerm) && (
            <button
              onClick={() => { setFilterStatus('all'); setFilterType('all'); setSearchTerm(''); setCurrentPage(1); }}
              className="px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-close-line text-sm" /> Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-foreground-500">Carregando faturas...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-14 h-14 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-receipt-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1">Nenhuma fatura encontrada</p>
            <p className="text-xs text-foreground-500">Tente ajustar os filtros de busca</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 dark:border-background-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Cliente</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Descrição</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tipo</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Valor</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Status</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Data</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">ID Pagamento</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((payment) => (
                    <tr key={payment.id} className="border-b border-background-50 dark:border-background-200 hover:bg-background-50/60 dark:hover:bg-background-200/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-[13px] font-semibold text-foreground-800 dark:text-foreground-200">{payment.user_name || '—'}</p>
                          {(payment.user_email || payment.company_name) && (
                            <p className="text-[11px] text-foreground-400 mt-0.5">
                              {payment.user_email || ''}{payment.user_email && payment.company_name ? ' · ' : ''}{payment.company_name || ''}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-[13px] text-foreground-700 dark:text-foreground-300">
                          {payment.description || typeLabels[payment.payment_type] || payment.payment_type}
                        </p>
                        {payment.credits > 0 && (
                          <p className="text-[10px] text-foreground-400 mt-0.5">+{payment.credits.toLocaleString('pt-BR')} créditos</p>
                        )}
                        {payment.extra_messages > 0 && (
                          <p className="text-[10px] text-foreground-400 mt-0.5">+{payment.extra_messages.toLocaleString('pt-BR')} mensagens</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-secondary-50 dark:bg-secondary-500/10 text-secondary-700 dark:text-secondary-400">
                          {typeLabels[payment.payment_type] || payment.payment_type}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right font-semibold text-foreground-900 dark:text-foreground-100 tabular-nums text-[13px]">
                        {formatCurrency(Number(payment.amount))}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-medium ${statusColors[payment.status] || 'bg-foreground-100 dark:bg-foreground-200 text-foreground-500'}`}>
                          {statusLabels[payment.status] || payment.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right text-[12px] text-foreground-500 whitespace-nowrap">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <code className="text-[10px] text-foreground-400 font-mono bg-background-50 dark:bg-background-200 px-1.5 py-0.5 rounded">
                          {payment.payment_id.slice(0, 14)}...
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-background-100 dark:border-background-200">
                <span className="text-xs text-foreground-500">
                  Mostrando {Math.min((currentPage - 1) * perPage + 1, filtered.length)}–{Math.min(currentPage * perPage, filtered.length)} de {filtered.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                        p === currentPage ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center gap-2 text-[10px] text-foreground-400">
        <i className="ri-shield-check-line text-sm" />
        <span>Processado via MercadoPago · Visão administrativa</span>
      </div>
    </div>
  );
}